// EditorBoardArea — the editor↔companion-surface split, in all five layout
// modes. Extracted from the viewer's inline `editorBoardArea` so Studio (and,
// later, the viewer) mount ONE split renderer instead of hand-rolling it.
//
// The EDITOR fills; the SURFACE (board / types / process / docs) is a
// collapsible "preview" panel on the right for split-right / editor-only.
// Collapsing the preview IS what sets editor-only (`collapsed` ↔ layout), so the
// drag gesture and the layout pill share one state. split-bottom / -top stack
// via VerticalSplit; view-only shows the surface alone.
import { useEffect, useRef, type ReactElement, type ReactNode } from "react";
import { ResizablePanel } from "../../components/ResizablePanel";
import { WIDTH_TIERS } from "../../components/widthTiers";
import { VerticalSplit } from "../../components/VerticalSplit";
import { type EditorLayout } from "./EditorLayoutMenu";

export interface EditorBoardAreaProps {
  readonly layout: EditorLayout;
  readonly onLayoutChange: (layout: EditorLayout) => void;
  /** The editor pane (Monaco / doc surface + its tab strip). */
  readonly editor: ReactNode;
  /** The companion surface (board / types / process / docs). */
  readonly surface: ReactNode;
  /** False when the active tab has no editor surface → the surface fills.
   *  Default true. */
  readonly hasEditor?: boolean;
  /** localStorage key namespace for the split fraction + preview width.
   *  Default matches the viewer's historical keys. */
  readonly persistNamespace?: string;
}

export function EditorBoardArea({
  layout,
  onLayoutChange,
  editor,
  surface,
  hasEditor = true,
  persistNamespace = "archlang.viewer",
}: EditorBoardAreaProps): ReactElement {
  // Remember the last real split so collapsing to editor-only can restore it.
  const lastSplitRef = useRef<EditorLayout>(
    layout === "editor-only" || layout === "view-only" ? "split-right" : layout,
  );
  useEffect(() => {
    if (layout !== "editor-only" && layout !== "view-only") {
      lastSplitRef.current = layout;
    }
  }, [layout]);

  const splitKey = `${persistNamespace}.editorSplit.fraction.v1`;
  const boardWidthKey = `${persistNamespace}.board.width.v1`;

  if (!hasEditor || layout === "view-only") return <>{surface}</>;

  if (layout === "split-bottom") {
    return <VerticalSplit storageKey={splitKey} top={editor} bottom={surface} />;
  }
  if (layout === "split-top") {
    return <VerticalSplit storageKey={splitKey} top={surface} bottom={editor} />;
  }

  // split-right (default) AND editor-only: editor fills, surface is the
  // collapsible preview panel. `collapsed` ↔ editor-only.
  return (
    <>
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">{editor}</div>
      <ResizablePanel
        side="right"
        ariaLabel="Preview"
        widthKey={boardWidthKey}
        collapsed={layout === "editor-only"}
        onCollapsedChange={(collapsed) =>
          onLayoutChange(collapsed ? "editor-only" : lastSplitRef.current)
        }
        showHandle={false}
        defaultWidth={560}
        {...WIDTH_TIERS.primaryPane}
        className="border-l border-slate-800 bg-slate-950"
      >
        {surface}
      </ResizablePanel>
    </>
  );
}
